import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type QueryDocumentSnapshot,
  type Timestamp,
} from 'firebase/firestore';
import type { TokenUsageEvent } from '@flintmint/core-shared/firestore/schemas';

import { getFirestore } from './firebase-service';

export interface TokenUsageMonthlyRollupDoc {
  userId: string;
  month: string;
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens?: number;
  estimatedCostUsd: number | null;
  callCount: number;
}

export interface ModelPricingSnapshotDoc {
  id: string;
  provider: string;
  model: string;
  rateKind: string;
  minInputTokens?: number;
  maxInputTokens?: number;
  inputUsdPer1M: number;
  outputUsdPer1M: number;
  cacheReadUsdPer1M?: number;
  sourceUrl: string;
  createdAt: Timestamp;
}

export async function fetchAllTokenUsageMonthlyRollups(): Promise<TokenUsageMonthlyRollupDoc[]> {
  const firestore = getFirestore();
  const snapshot = await getDocs(collection(firestore, 'tokenUsageMonthlyRollups'));
  return snapshot.docs.map((doc) => doc.data() as TokenUsageMonthlyRollupDoc);
}

export async function fetchTokenUsageMonthlyRollupsByUser(userId: string): Promise<TokenUsageMonthlyRollupDoc[]> {
  const firestore = getFirestore();
  const q = query(collection(firestore, 'tokenUsageMonthlyRollups'), where('userId', '==', userId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => doc.data() as TokenUsageMonthlyRollupDoc);
}

export const AI_HISTORY_PAGE_SIZE = 25;

/** One page of the user's events, newest first; reads one extra event so `hasMore` is false when exactly a page remains. */
export async function fetchAiHistoryPage(
  userId: string,
  after: QueryDocumentSnapshot | null
): Promise<{ events: (TokenUsageEvent & { id: string })[]; cursor: QueryDocumentSnapshot | null; hasMore: boolean }> {
  const firestore = getFirestore();
  const q = query(
    collection(firestore, 'tokenUsageEvents'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    ...(after ? [startAfter(after)] : []),
    limit(AI_HISTORY_PAGE_SIZE + 1)
  );
  const snapshot = await getDocs(q);
  const pageDocs = snapshot.docs.slice(0, AI_HISTORY_PAGE_SIZE);
  return {
    events: pageDocs.map((doc) => ({ id: doc.id, ...(doc.data() as TokenUsageEvent) })),
    cursor: pageDocs.at(-1) ?? null,
    hasMore: snapshot.docs.length > AI_HISTORY_PAGE_SIZE,
  };
}

export async function fetchMissingPricingTokenUsageEventsByUser(userId: string): Promise<(TokenUsageEvent & { id: string })[]> {
  const firestore = getFirestore();
  const q = query(
    collection(firestore, 'tokenUsageEvents'),
    where('userId', '==', userId),
    where('pricingStatus', '==', 'missingPricingSnapshot')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as TokenUsageEvent) }));
}

export async function fetchMissingPricingTokenUsageEvents(): Promise<(TokenUsageEvent & { id: string })[]> {
  const firestore = getFirestore();
  const q = query(collection(firestore, 'tokenUsageEvents'), where('pricingStatus', '==', 'missingPricingSnapshot'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as TokenUsageEvent) }));
}

export async function fetchAllModelPricingSnapshots(): Promise<ModelPricingSnapshotDoc[]> {
  const firestore = getFirestore();
  const snapshot = await getDocs(collection(firestore, 'modelPricingSnapshots'));
  return snapshot.docs.map((doc) => ({ id: doc.id, ...(doc.data() as Omit<ModelPricingSnapshotDoc, 'id'>) }));
}
